import { useEffect, useState } from 'react'

/**
 * Hook to calculate dynamic maxHeight based on window/viewport height minus an offset.
 * Useful for dropdowns and menus that need to stay above the keyboard on mobile.
 *
 * @param offset - The offset in pixels to subtract from the window height (default: 0)
 * @returns The maxHeight value as a string (e.g., "500px")
 */
export const useDynamicMaxHeight = (offset: number = 0): string => {
  const [maxHeight, setMaxHeight] = useState<string>('200px')

  useEffect(() => {
    const updateMaxHeight = () => {
      if (typeof window === 'undefined') return
      const windowHeight = window.visualViewport?.height || window.innerHeight
      setMaxHeight(`${windowHeight - offset}px`)
    }

    updateMaxHeight()

    // Listen to visual viewport changes (keyboard open/close) and window resize
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateMaxHeight)
      window.addEventListener('resize', updateMaxHeight)
    }

    return () => {
      if (typeof window !== 'undefined' && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateMaxHeight)
        window.removeEventListener('resize', updateMaxHeight)
      }
    }
  }, [offset])

  return maxHeight
}

export default useDynamicMaxHeight
